import React, { useState } from 'react';
import { useGetList, useTranslate } from 'react-admin';
import { Box, Button, Card, CardContent, Chip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { MarkRefundedByHandButton } from './MarkRefundedByHandButton';
import { MarkDoubleRefundHandledButton } from './MarkDoubleRefundHandledButton';
import { RefundStateChip } from './KioskTransactionsList';
import { NEEDS_ACTION_GROUPS, NEEDS_ACTION_RESOURCE, needsActionTiming } from './needsAction';
import { paidWith } from './refundState';
import type { NeedsActionGroup, NeedsActionRow } from './types';

const SECTION_PAGE = 25;
const money = (value: number | null) => (value === null ? '-' : `HK$${value.toFixed(2)}`);

const NeedsActionItem = ({ row, now }: { row: NeedsActionRow; now: number }) => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const t = (key: string, options?: any) => translate(`resources.kiosk-transactions.${key}`, options);
  const paid = paidWith(row, translate);
  return (
    <Box
      data-testid={`needs-action-row-${row.id}`}
      sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' }, justifyContent: 'space-between', gap: 1, py: 1.5, borderBottom: '1px solid #F0F0F0' }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 700 }}>{row.receiptReference}</Typography>
          <RefundStateChip state={row.refundState} />
        </Box>
        <Typography variant="caption" color="text.secondary" display="block">
          {`${paid === null ? t(`methods.${row.method}`) : paid} · ${row.kioskName} · ${row.siteName}`}
        </Typography>
        <Typography variant="body2">{`${t(`needsAction.amountLabels.${row.group}`)}: ${money(row.refundAmount)} · ${needsActionTiming(row, now, translate)}`}</Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {row.group === 'owed' && <MarkRefundedByHandButton record={row} />}
        {row.group === 'refunded_twice' && <MarkDoubleRefundHandledButton record={row} />}
        <Button variant="outlined" size="small" onClick={() => navigate(`/kiosk-transactions/${row.id}/show`)}>{t('needsAction.open')}</Button>
      </Box>
    </Box>
  );
};

export const NeedsActionSection = ({ group }: { group: NeedsActionGroup }) => {
  const translate = useTranslate();
  const [perPage, setPerPage] = useState(SECTION_PAGE);
  const { data, total, isPending, error } = useGetList<NeedsActionRow>(NEEDS_ACTION_RESOURCE, {
    filter: { group },
    pagination: { page: 1, perPage },
    sort: { field: 'since', order: 'ASC' },
  });
  const t = (key: string) => translate(`resources.kiosk-transactions.needsAction.${key}`);
  const now = Date.now();
  return (
    <Card data-testid={`needs-action-${group}`}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">{t(`groups.${group}`)}</Typography>
          {total !== undefined && <Chip size="small" label={total} color={group !== 'waiting' && total > 0 ? 'error' : 'default'} />}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{t(`hints.${group}`)}</Typography>
        {error ? <Typography color="error" variant="body2">{t('loadFailed')}</Typography> : null}
        {!error && !isPending && data !== undefined && data.length === 0 && <Typography variant="body2" color="text.secondary">{t('empty')}</Typography>}
        {!error && data !== undefined && data.map((row) => <NeedsActionItem key={row.id} row={row} now={now} />)}
        {!error && data !== undefined && total !== undefined && data.length < total && (
          <Button size="small" onClick={() => setPerPage(perPage + SECTION_PAGE)}>{t('showMore')}</Button>
        )}
      </CardContent>
    </Card>
  );
};

export const KioskNeedsActionTab = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: { xs: 1, md: 2 } }}>
    {NEEDS_ACTION_GROUPS.map((group) => <NeedsActionSection key={group} group={group} />)}
  </Box>
);
